/*
 * Copyright (c) 2021. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import _ from 'lodash';
import z from 'zod';
import { parsePercent } from '../utils';
import { formNewConfigPayload, } from './config';
import { componentIdSchema } from './core';
export const horizonProfileAzimuths = _.range(0, 361, 7.5);
export var AuxWhenRunning;
(function (AuxWhenRunning) {
    AuxWhenRunning["DAY"] = "day";
    AuxWhenRunning["NIGHT"] = "night";
    AuxWhenRunning["DAY_NIGHT"] = "day_and_night";
})(AuxWhenRunning || (AuxWhenRunning = {}));
export const siteAspectRatios = ['1:1', '1:2', '1:3', '1:4', '1:5', '2:3', '3:4'];
export const RACKING_TYPES = {
    fixed: 'Fixed Tilt',
    sat: 'Single Axis Tracker',
    ew: 'East-West',
};
export const manufacturerRecordComponentTypes = [
    'pvinverter',
    'pvmodule',
    'battery',
    'essinverter',
    'mvt',
];
export const getPvInverterLabel = (inverter) => inverter ? `${inverter.manufacturer} - ${inverter.model}` : '';
export const getPvInverterRecordLabel = (inverter) => getPvInverterLabel(inverter?.data);
export const getPvModuleLabel = (pvModule, id) => (pvModule ? `${pvModule.manufacturer} - ${pvModule.model} - ${id?.slice(-5)}` : '');
export const getPvModuleRecordLabel = (pvModule) => getPvModuleLabel(pvModule?.data, pvModule?.id);
export const calculatePvAcMw = (numOfInverters, pvPaco) => (numOfInverters * pvPaco) / 1e6;
export const calculateSitePvMwAc = (inverters) => _.sum(inverters.map((inv) => calculatePvAcMw(inv.number, inv.pvinverter.paco ?? 0)));
export const calculatePvDcMw = (modulesPerString, numOfStrings, numOfInverters, moduleWp) => (modulesPerString * numOfInverters * numOfStrings * moduleWp) / 1e6;
export const calculateSitePvMwDc = (inverters) => _.sum(inverters.map((inv) => calculatePvDcMw(inv.arrays[0].modules_per_string, inv.arrays[0].strings, inv.number, inv.arrays[0].pvmodule.nominal_power ?? 0)));
export const calculateDcAcRatio = (poi_mw_ac, dcCapacity) => _.round(dcCapacity / poi_mw_ac, 3);
export const calculateIlr = (acCapacity, dcCapacity) => acCapacity > 0 ? _.round(dcCapacity / acCapacity, 3) : 0;
export const determineRackingType = (is_ew, isTracker) => {
    if (is_ew && !isTracker) {
        return 'ew';
    }
    if (!is_ew && !isTracker) {
        return 'fixed';
    }
    return 'sat';
};
export const transformAuxLossesToPerc = (aux) => parsePercent(_.sumBy(aux, 'consumption_percent') ?? 0, '0');
export const transformAuxLossesToKw = (aux) => _.round(_.sumBy(aux, 'consumption') ?? 0, 6).toString();
const trackerSchema = z
    .object({
    backtracking: z.boolean(),
    type: z.literal('singleaxis'),
    max_angle: z.number(),
})
    .strict();
const moduleDegradationSchema = z
    .object({
    rate: z.number().min(0).max(1).nullable(),
})
    .strict();
const basePvArraySchema = z
    .object({
    azimuth: z.number(),
    modules_per_string: z.number().int(),
    gcr: z.number(),
    strings: z.number().int(),
    tilt: z.number().nullable(),
    module_lower_edge_height: z.number().min(0).nullable(),
    module_orientation: z.enum(['portrait', 'landscape']).nullable(),
    num_modules_per_unit: z.number().int().min(1).nullable(),
    tracker: trackerSchema.nullable(),
    soiling_losses: z.union([z.number(), z.array(z.number())]).nullable(),
    current_voltage_mismatch: z.number().min(0).max(1).nullable(),
    backside_irradiance_mismatch: z.number().min(0).max(1).nullable(),
    module_quality: z.number().min(-1).max(1).nullable(),
    lid: z.number().min(0).max(1).nullable(),
    module_degradation: moduleDegradationSchema.nullable(),
    dc_wire_losses: z.number().min(0).max(1).nullable(),
})
    .strict();
export const pvModuleIdSchema = componentIdSchema.extend({
    nominal_power: z.number().optional(),
});
export const pvArrayIdSchema = basePvArraySchema
    .extend({
    pvmodule: pvModuleIdSchema,
})
    .strict();
const transformerSchema = z
    .object({
    number: z.number().min(0).nullable().optional(),
    rating_kva: z.number().min(0).nullable(),
    total_loss_full_load_percent: z.number().min(0).max(1).nullable(),
    no_load_percent: z.number().min(0).max(1).nullable(),
    full_load_percent: z.number().min(0).max(1).nullable(),
    xr_short_circuit_voltage_percent: z.number().min(0).max(1).nullable(),
})
    .strict();
const baseInverterBlockSchema = z
    .object({
    number: z.number().int().min(1),
    power_factor: z.number().min(0).max(1).nullable(),
    transformer: transformerSchema.nullable(),
    ac_wire_losses: z.number().min(0).max(1).nullable(),
    collection_system_voltage: z.number().min(0).optional(),
})
    .strict();
export const pvInverterIdSchema = componentIdSchema.extend({
    paco: z.number().optional(),
});
export const inverterBlockIdSchema = baseInverterBlockSchema
    .extend({
    pvinverter: pvInverterIdSchema,
    arrays: z.array(pvArrayIdSchema),
})
    .strict();
const auxiliarySchema = z
    .object({
    consumption: z.number().nullable().optional(),
    consumption_percent: z.number().nullable().optional(),
    when: z.enum(AuxWhenRunning),
})
    .strict();
const lossesSchema = z
    .object({
    hv_transformer: transformerSchema.nullable(),
    transmission_line: z.number().min(0).max(1).nullable(),
    auxiliaries: z.array(auxiliarySchema),
    availability: z.number().min(0).max(1).nullable(),
})
    .strict();
const soilingSchema = z
    .object({
    soiling_rate: z
        .union([z.number(), z.array(z.number())])
        .nullable()
        .optional(),
    number_of_washes: z.number().nullable().optional(),
    grace_period: z.number().nullable().optional(),
    cleaning_threshold: z.number().nullable().optional(),
})
    .strict();
const baseSiteSchema = z
    .object({
    albedo: z.union([z.number(), z.array(z.number())]),
    horizon_profile: z
        .object({
        profile: z.array(z.tuple([z.number(), z.number()])),
        coordinates: z.tuple([z.number(), z.number()]),
    })
        .strict()
        .nullable()
        .optional(),
    poi_mw_ac: z.number(),
    losses: lossesSchema.nullable(),
    soiling: soilingSchema.optional(),
    target_pf_poi: z.number().nullable().optional(),
    design_temperature: z.number().nullable().optional(),
    is_ew: z.boolean().optional(),
    aspect_ratio: z.enum(siteAspectRatios).optional(),
    land_multiplier: z.number().min(1).max(5).optional(),
    include_cap_bank: z.boolean().optional(),
    cap_bank_kvar: z.number().nullable().optional(),
})
    .strict();
export const siteIdSchema = baseSiteSchema
    .extend({
    inverters: z.array(inverterBlockIdSchema),
})
    .strict();
const uncertaintiesSchema = z
    .object({
    solar_resource_model_pct_stddev: z.number(),
    solar_resource_variability_pct_stddev: z.number(),
    pvsim_energy_model_pct_stddev: z.number(),
})
    .strict();
const simulationParametersSchema = z
    .object({
    inverter_derates: z.boolean().optional(),
    albedo_override: z.boolean().optional(),
    no_poi_clipping: z.boolean().optional(),
    calculate_far_shading: z.boolean().optional(),
    calculate_spectral_mismatch: z.boolean().optional(),
    include_cap_bank: z.boolean().optional(),
    save_long_term_results_to_gcs: z.boolean().optional(),
    simulation_period_years: z.number().optional(),
    simulation_start: z.string().optional(),
    uncertainties: uncertaintiesSchema.optional(),
})
    .strict();
export const pvSimulationRequestSchema = siteIdSchema
    .extend({
    weather: componentIdSchema
        .extend({
        rainfall_weather_id: z.string().optional(),
    })
        .strict(),
    simulation: simulationParametersSchema,
    custom_loss_id: z.string().nullable().optional(),
    custom_loss_comment: z.string().nullable().optional(),
})
    .strict();
export const newPvSimConfigPayload = formNewConfigPayload(pvSimulationRequestSchema);
//# sourceMappingURL=pv-sim.js.map